import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { PROPOSAL_STATUSES, STATUS_COLORS, fmtINR } from "../lib/constants";
import { PROPOSAL_TYPES, PROPOSAL_TYPE_META } from "../lib/templates";
import { Modal, Input } from "./Leads";
import {
  Plus, FileText, Sparkles, Link2, Download, Edit3, Eye, ChevronDown, ChevronRight,
  Send, CheckCircle2, XCircle, ArrowRight, Save, History, Trash2, Mail, Receipt,
} from "lucide-react";
import { toast } from "sonner";

const WORKFLOW_NEXT = {
  Draft: [{ to: "Submitted for Approval", label: "Submit for Approval", icon: Send, cls: "bg-amber-600" }],
  "Submitted for Approval": [
    { to: "Approved", label: "Approve", icon: CheckCircle2, cls: "bg-emerald-700" },
    { to: "Rejected", label: "Reject", icon: XCircle, cls: "bg-rose-600" },
  ],
  Approved: [{ to: "Sent", label: "Mark Sent to Client", icon: Send, cls: "bg-sky-700" }],
  Sent: [
    { to: "Accepted", label: "Mark Accepted", icon: CheckCircle2, cls: "bg-emerald-700" },
    { to: "Rejected", label: "Mark Rejected", icon: XCircle, cls: "bg-rose-600" },
  ],
  Rejected: [{ to: "Draft", label: "Re-open Draft", icon: Edit3, cls: "bg-stone-700" }],
  Accepted: [],
  Closed: [],
};

export default function Proposals() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");
  const [newOpen, setNewOpen] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [viewProp, setViewProp] = useState(null);

  const load = async () => {
    const [p, l] = await Promise.all([api.get("/proposals"), api.get("/leads")]);
    setItems(p.data);
    setLeads(l.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "All" ? items : items.filter((p) => p.status === filter);
  const leadName = (id) => leads.find((l) => l.id === id)?.company_name;

  return (
    <div className="space-y-6 animate-fade-up" style={{ fontFamily: "Poppins, sans-serif" }}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Commercial</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F2A4A]">Proposals</h1>
          <p className="text-stone-500 mt-1 text-sm">{items.length} proposals · PTESPL/FY/####  ·  8 service templates</p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-proposal-btn"
                className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium" style={{ background: "#0F2A4A" }}>
          <Plus className="w-4 h-4" /> New Proposal
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["All", ...PROPOSAL_STATUSES].map((t) => (
          <button key={t} onClick={() => setFilter(t)} data-testid={`prop-filter-${t}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === t
              ? "bg-[#0F2A4A] text-white border-[#0F2A4A]"
              : "bg-white border-stone-200 text-stone-600 hover:border-[#72BE44]"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Proposal #</th>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-4 py-3 font-semibold">Client / Project</th>
              <th className="text-left px-4 py-3 font-semibold">Lead</th>
              <th className="text-right px-4 py-3 font-semibold">Fees</th>
              <th className="text-left px-4 py-3 font-semibold">Ver</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-center px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`proposal-${p.id}`} className="border-t border-stone-100 hover:bg-[#72BE44]/5">
                <td className="px-4 py-3 font-mono text-xs text-[#0F2A4A] font-semibold">{p.proposal_number}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                    {PROPOSAL_TYPE_META[p.proposal_type]?.label || p.proposal_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#0F2A4A]">{p.project_name}</div>
                  <div className="text-xs text-stone-500">{p.client_name}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {p.lead_id ? (
                    <span className="inline-flex items-center gap-1 text-[#72BE44] font-medium"><Link2 className="w-3 h-3" />{leadName(p.lead_id) || "Linked"}</span>
                  ) : <span className="text-stone-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-[#0F2A4A]">{fmtINR(p.fees)}</td>
                <td className="px-4 py-3 text-xs text-stone-600">v{p.version || 1}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${STATUS_COLORS[p.status] || "bg-stone-100"}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-1">
                    <IconBtn title="View" onClick={() => setViewProp(p)} testid={`prop-view-${p.id}`}><Eye className="w-3.5 h-3.5" /></IconBtn>
                    <IconBtn title="Edit" onClick={() => setEditProp(p)} testid={`prop-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5" /></IconBtn>
                    <a href={`${API}/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`prop-pdf-${p.id}`}
                       title="Download PDF" className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A] transition">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-stone-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" /> No proposals yet
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {newOpen && <NewProposalModal leads={leads} onClose={() => setNewOpen(false)} onCreated={(p) => { setNewOpen(false); load(); setEditProp(p); }} />}
      {editProp && <ProposalEditor proposal={editProp} onClose={() => setEditProp(null)} onSaved={() => { load(); }} />}
      {viewProp && <ProposalEditor proposal={viewProp} readOnly onClose={() => setViewProp(null)} onSaved={() => {}} />}
    </div>
  );
}

function IconBtn({ children, title, onClick, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
            className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A] transition">{children}</button>
  );
}

function NewProposalModal({ leads, onClose, onCreated }) {
  const [form, setForm] = useState({
    lead_id: "",
    proposal_type: "Fresh_EC_CTE",
    client_name: "",
    project_name: "",
    project_location: "",
    client_email: "",
    client_phone: "",
  });

  const pickLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (!l) { setForm({ ...form, lead_id: "" }); return; }
    setForm({
      ...form,
      lead_id: id,
      client_name: l.company_name || "",
      project_name: l.project_name || l.project_type || "",
      project_location: l.location || "",
      client_email: l.email || "",
      client_phone: l.phone || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      let resp;
      if (form.lead_id) {
        resp = await api.post(`/proposals/from-lead/${form.lead_id}`, { proposal_type: form.proposal_type });
      } else {
        resp = await api.post("/proposals", { ...form });
      }
      toast.success(`Proposal ${resp.data.proposal_number} created`);
      onCreated(resp.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create proposal");
    }
  };

  return (
    <Modal title="New Proposal" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="bg-[#72BE44]/10 border border-[#72BE44]/30 rounded-lg p-3 text-xs text-[#0F2A4A] flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#72BE44]" />
          <span>Selecting a template auto-fills intro, applicability, scope of work, professional fees, payment terms and standard T&Cs. You can customize any section before sending.</span>
        </div>

        <SelectField label="Link to Lead (optional — auto-fills client/project)" value={form.lead_id} onChange={pickLead}
          options={[{ value: "", label: "— None —" }, ...leads.map((l) => ({ value: l.id, label: `${l.lead_number || l.id.slice(0, 6)} · ${l.company_name}` }))]}
          testid="proposal-lead" />

        <SelectField label="Proposal Template" value={form.proposal_type} onChange={(v) => setForm({ ...form, proposal_type: v })}
          options={PROPOSAL_TYPES.map((t) => ({ value: t, label: PROPOSAL_TYPE_META[t].label }))} testid="proposal-type" />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Client Name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} required testid="proposal-client" />
          <Input label="Project Name" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} required testid="proposal-project-name" />
        </div>
        <Input label="Project Location" value={form.project_location} onChange={(v) => setForm({ ...form, project_location: v })} testid="proposal-loc" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Client Email" type="email" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} testid="proposal-email" />
          <Input label="Client Phone" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} testid="proposal-phone" />
        </div>

        <button type="submit" data-testid="proposal-submit"
                className="w-full text-white py-2.5 rounded-lg font-medium" style={{ background: "#0F2A4A" }}>
          Generate Proposal
        </button>
      </form>
    </Modal>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:border-[#72BE44] focus:outline-none text-sm">
        {options.map((o) => typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ProposalEditor({ proposal, readOnly = false, onClose, onSaved }) {
  const [p, setP] = useState({ ...proposal });
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [openSections, setOpenSections] = useState({ cover: true, intro: true, scope: true, fees: true, terms: false, conclusion: false });

  useEffect(() => { setP({ ...proposal }); }, [proposal]);
  useEffect(() => { if (showVersions) api.get(`/proposals/${proposal.id}/versions`).then((r) => setVersions(r.data)); }, [showVersions, proposal.id]);

  const upd = (patch) => setP((s) => ({ ...s, ...patch }));
  const toggle = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  const save = async () => {
    try {
      await api.put(`/proposals/${p.id}`, p);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
  };

  const saveVersion = async () => {
    await save();
    await api.post(`/proposals/${p.id}/save-version`);
    toast.success("Version snapshot saved");
    setShowVersions(true);
  };

  const transition = async (to) => {
    try {
      let notes = "";
      if (to === "Rejected") {
        notes = window.prompt("Reason for rejection?") || "";
      }
      const { data } = await api.post(`/proposals/${p.id}/transition`, { to, notes });
      setP(data);
      toast.success(`Status → ${to}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const convertToWO = async () => {
    if (!window.confirm(`Convert this Accepted proposal to Work Order + Project?`)) return;
    try {
      const { data } = await api.post(`/proposals/${p.id}/convert-to-wo`, {});
      toast.success(`Work Order ${data.wo_number} created`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const nextActions = WORKFLOW_NEXT[p.status] || [];

  const [emailing, setEmailing] = useState("");
  const emailDoc = async (kind) => {
    setEmailing(kind);
    try {
      const { data } = await api.post(`/emails/${kind}/${p.id}`);
      toast.success(`${kind === "quotation" ? "Quotation" : "Proposal"} emailed to ${data.to}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setEmailing(""); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-stone-50 w-full max-w-5xl h-full overflow-y-auto shadow-2xl animate-slide-in" onClick={(e) => e.stopPropagation()} data-testid="proposal-editor">

        {/* HEADER */}
        <div className="sticky top-0 bg-[#0F2A4A] text-white p-5 z-10 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#72BE44] font-bold">{PROPOSAL_TYPE_META[p.proposal_type]?.label || p.proposal_type}</div>
            <div className="font-mono text-sm mt-1">{p.proposal_number}</div>
            <h2 className="text-xl font-bold mt-1">{p.project_name}</h2>
            <div className="text-xs text-slate-300 mt-1">{p.client_name} · v{p.version || 1}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-bold ${STATUS_COLORS[p.status]}`}>{p.status}</span>
            <button onClick={onClose} className="text-xs text-white/70 hover:text-white px-2 py-1">Close</button>
          </div>
        </div>

        {/* ACTION BAR */}
        <div className="bg-white border-b border-stone-200 px-5 py-3 flex items-center gap-2 flex-wrap sticky top-[120px] z-[5]">
          {!readOnly && (
            <button onClick={save} data-testid="prop-save"
              className="flex items-center gap-1.5 bg-stone-200 hover:bg-stone-300 text-[#0F2A4A] px-3 py-1.5 rounded text-xs font-medium">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          )}
          {!readOnly && (
            <button onClick={saveVersion} data-testid="prop-save-version"
              className="flex items-center gap-1.5 bg-stone-200 hover:bg-stone-300 text-[#0F2A4A] px-3 py-1.5 rounded text-xs font-medium">
              <History className="w-3.5 h-3.5" /> Save Version
            </button>
          )}
          <a href={`${API}/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" data-testid="prop-download-pdf"
            className="flex items-center gap-1.5 bg-[#72BE44] hover:bg-[#5da636] text-white px-3 py-1.5 rounded text-xs font-medium">
            <Download className="w-3.5 h-3.5" /> Download PDF
          </a>
          <a href={`${API}/proposals/${p.id}/quotation-pdf`} target="_blank" rel="noreferrer" data-testid="prop-quotation-pdf"
            className="flex items-center gap-1.5 bg-[#0F2A4A] hover:bg-[#1a3a5f] text-white px-3 py-1.5 rounded text-xs font-medium">
            <Receipt className="w-3.5 h-3.5" /> Quotation PDF
          </a>
          <button onClick={() => emailDoc("proposal")} disabled={!!emailing} data-testid="prop-email-btn"
            className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-60">
            <Mail className="w-3.5 h-3.5" /> {emailing === "proposal" ? "Sending..." : "Email Proposal"}
          </button>
          <button onClick={() => emailDoc("quotation")} disabled={!!emailing} data-testid="prop-email-quotation-btn"
            className="flex items-center gap-1.5 bg-violet-500 hover:bg-violet-600 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-60">
            <Mail className="w-3.5 h-3.5" /> {emailing === "quotation" ? "Sending..." : "Email Quotation"}
          </button>
          <div className="h-4 w-px bg-stone-300 mx-1" />
          {!readOnly && nextActions.map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.to} onClick={() => transition(a.to)} data-testid={`prop-action-${a.to.replace(/\s+/g, "-").toLowerCase()}`}
                className={`flex items-center gap-1.5 text-white px-3 py-1.5 rounded text-xs font-medium ${a.cls}`}>
                <Icon className="w-3.5 h-3.5" /> {a.label}
              </button>
            );
          })}
          {!readOnly && p.status === "Accepted" && !p.wo_id && (
            <button onClick={convertToWO} data-testid="prop-convert-wo"
              className="flex items-center gap-1.5 bg-[#0F2A4A] text-white px-3 py-1.5 rounded text-xs font-medium">
              <ArrowRight className="w-3.5 h-3.5" /> Generate Work Order
            </button>
          )}
          {p.wo_id && <a href="/work-orders" className="text-[11px] text-[#72BE44] font-medium underline">View Work Order →</a>}
        </div>

        <div className="p-5 space-y-4">
          {showVersions && (
            <Card title={`Version History (${versions.length})`} onToggle={() => setShowVersions(false)} open>
              {versions.length === 0 ? <p className="text-xs text-stone-500">No version snapshots yet.</p> :
                <ul className="space-y-2">
                  {versions.map((v) => (
                    <li key={v.version} className="text-xs flex items-center gap-3 border-b border-stone-100 pb-2">
                      <span className="font-mono text-stone-500">v{v.version}</span>
                      <span className="text-stone-700">{(v.snapshot_at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className="text-stone-500">Fees: ₹{(v.fees || 0).toLocaleString("en-IN")}</span>
                      <span className="ml-auto text-stone-500">{v.status}</span>
                    </li>
                  ))}
                </ul>}
            </Card>
          )}

          {/* COVER PAGE */}
          <Card title="Cover Page (auto-fetched from Lead)" open={openSections.cover} onToggle={() => toggle("cover")}>
            <div className="grid grid-cols-2 gap-3">
              <ED label="Title" value={p.title} onChange={(v) => upd({ title: v })} ro={readOnly} testid="ed-title" />
              <ED label="Client Name" value={p.client_name} onChange={(v) => upd({ client_name: v })} ro={readOnly} testid="ed-client" />
              <ED label="Project Name" value={p.project_name} onChange={(v) => upd({ project_name: v })} ro={readOnly} testid="ed-project" />
              <ED label="Project Location" value={p.project_location} onChange={(v) => upd({ project_location: v })} ro={readOnly} testid="ed-loc" />
              <ED label="Client Email" value={p.client_email} onChange={(v) => upd({ client_email: v })} ro={readOnly} testid="ed-email" />
              <ED label="Client Phone" value={p.client_phone} onChange={(v) => upd({ client_phone: v })} ro={readOnly} testid="ed-phone" />
            </div>
          </Card>

          {/* 1. Introduction & Applicability */}
          <Card title="1. Introduction & Applicability" open={openSections.intro} onToggle={() => toggle("intro")}>
            <ED label="Introduction Paragraph" value={p.intro} onChange={(v) => upd({ intro: v })} ro={readOnly} textarea rows={4} testid="ed-intro" />
            <ED label="Applicability Note" value={p.applicability_note} onChange={(v) => upd({ applicability_note: v })} ro={readOnly} textarea rows={3} testid="ed-app-note" />

            <Label>Applicability Rows</Label>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-stone-200 rounded">
                <thead className="bg-[#0F2A4A] text-white">
                  <tr>
                    <th className="px-2 py-1 text-left">#</th>
                    <th className="px-2 py-1 text-left">Particular</th>
                    <th className="px-2 py-1 text-left">Applicability</th>
                    <th className="px-2 py-1 text-left">Category</th>
                    <th className="px-2 py-1 text-left">Authority</th>
                    {!readOnly && <th className="w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {(p.applicability_rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      {["sno", "particular", "applicability", "category", "authority"].map((k) => (
                        <td key={k} className="p-1">
                          {readOnly ? <span>{r[k]}</span> :
                            <input value={r[k] || ""} onChange={(e) => {
                              const rows = [...p.applicability_rows]; rows[i] = { ...rows[i], [k]: e.target.value };
                              upd({ applicability_rows: rows });
                            }} className="w-full px-1 py-0.5 border-0 bg-stone-50 rounded text-xs focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#72BE44]" />}
                        </td>
                      ))}
                      {!readOnly && (
                        <td className="p-1">
                          <button onClick={() => upd({ applicability_rows: p.applicability_rows.filter((_, x) => x !== i) })} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <button onClick={() => upd({ applicability_rows: [...(p.applicability_rows || []), { sno: "", particular: "", applicability: "", category: "", authority: "" }] })}
                className="mt-2 text-xs text-[#72BE44] hover:underline">+ Add Row</button>
            )}
            <ED label="Applicability Footnote" value={p.applicability_footnote} onChange={(v) => upd({ applicability_footnote: v })} ro={readOnly} testid="ed-app-foot" />
          </Card>

          {/* 2. Scope of Work */}
          <Card title="2. Scope of Work" open={openSections.scope} onToggle={() => toggle("scope")}>
            {(p.scope_sections || []).map((sec, si) => (
              <div key={si} className="mb-4 border border-stone-200 rounded p-3 bg-white">
                <ED label="Section Title" value={sec.title} onChange={(v) => {
                  const ss = [...p.scope_sections]; ss[si] = { ...ss[si], title: v }; upd({ scope_sections: ss });
                }} ro={readOnly} testid={`scope-title-${si}`} />
                {sec.rows.map((row, ri) => (
                  <div key={ri} className="mt-3 border-l-2 border-[#72BE44] pl-3">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-1">
                        <Label>Sr.</Label>
                        {readOnly ? <div>{row.sr_no}</div> :
                          <input type="number" value={row.sr_no} onChange={(e) => {
                            const ss = [...p.scope_sections]; ss[si].rows[ri] = { ...row, sr_no: Number(e.target.value) }; upd({ scope_sections: ss });
                          }} className="w-full px-2 py-1 bg-stone-50 border border-stone-200 rounded text-xs" />}
                      </div>
                      <div className="col-span-11">
                        <ED label="Description" value={row.description} onChange={(v) => {
                          const ss = [...p.scope_sections]; ss[si].rows[ri] = { ...row, description: v }; upd({ scope_sections: ss });
                        }} ro={readOnly} testid={`scope-desc-${si}-${ri}`} />
                      </div>
                    </div>
                    <ED label="Scope Items (one per line)" textarea rows={6}
                      value={(row.scope_items || []).join("\n")}
                      onChange={(v) => {
                        const ss = [...p.scope_sections]; ss[si].rows[ri] = { ...row, scope_items: v.split("\n").map((s) => s.trim()).filter(Boolean) }; upd({ scope_sections: ss });
                      }} ro={readOnly} testid={`scope-items-${si}-${ri}`} />
                    {!readOnly && (
                      <button onClick={() => {
                        const ss = [...p.scope_sections]; ss[si].rows = ss[si].rows.filter((_, x) => x !== ri); upd({ scope_sections: ss });
                      }} className="text-xs text-rose-600 hover:underline">Remove Row</button>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <button onClick={() => {
                    const ss = [...p.scope_sections]; ss[si].rows = [...ss[si].rows, { sr_no: ss[si].rows.length + 1, description: "", scope_items: [] }]; upd({ scope_sections: ss });
                  }} className="mt-2 text-xs text-[#72BE44] hover:underline">+ Add Scope Row</button>
                )}
              </div>
            ))}
            {!readOnly && (
              <button onClick={() => upd({ scope_sections: [...(p.scope_sections || []), { title: "New Section", rows: [] }] })}
                className="text-xs text-[#0F2A4A] hover:underline font-medium">+ Add Section</button>
            )}
          </Card>

          {/* 3. Professional Fees */}
          <Card title="3. Professional Fees & Payment Terms" open={openSections.fees} onToggle={() => toggle("fees")}>
            <Label>Fee Items</Label>
            <table className="w-full text-xs border border-stone-200 rounded">
              <thead className="bg-[#0F2A4A] text-white">
                <tr><th className="px-2 py-1 text-left">Sr</th><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Amount (₹)</th>{!readOnly && <th className="w-8"></th>}</tr>
              </thead>
              <tbody>
                {(p.fee_items || []).map((fi, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="px-2 py-1 text-stone-500">{i + 1}</td>
                    <td className="p-1">
                      {readOnly ? <span>{fi.description}</span> :
                        <input value={fi.description || ""} onChange={(e) => {
                          const fis = [...p.fee_items]; fis[i] = { ...fis[i], description: e.target.value }; upd({ fee_items: fis });
                        }} className="w-full px-1 py-0.5 border-0 bg-stone-50 rounded text-xs focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#72BE44]" />}
                    </td>
                    <td className="p-1 text-right">
                      {readOnly ? <span>{fi.amount?.toLocaleString("en-IN")}</span> :
                        <input type="number" value={fi.amount || 0} onChange={(e) => {
                          const fis = [...p.fee_items]; fis[i] = { ...fis[i], amount: Number(e.target.value) }; upd({ fee_items: fis });
                        }} className="w-32 px-2 py-0.5 border-0 bg-stone-50 rounded text-xs text-right focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#72BE44]" />}
                    </td>
                    {!readOnly && (
                      <td className="p-1">
                        <button onClick={() => upd({ fee_items: p.fee_items.filter((_, x) => x !== i) })} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button>
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="bg-stone-100 font-semibold">
                  <td colSpan={2} className="px-2 py-1 text-right">TOTAL</td>
                  <td className="px-2 py-1 text-right">{(p.fee_items || []).reduce((a, x) => a + (Number(x.amount) || 0), 0).toLocaleString("en-IN")}</td>
                  {!readOnly && <td></td>}
                </tr>
              </tbody>
            </table>
            {!readOnly && (
              <button onClick={() => upd({ fee_items: [...(p.fee_items || []), { description: "", amount: 0 }] })}
                className="mt-2 text-xs text-[#72BE44] hover:underline">+ Add Fee Item</button>
            )}
            <p className="text-xs text-stone-500 italic mt-1">Taxes – Extra as actual</p>

            <Label>Payment Milestones (one per line)</Label>
            <ED textarea rows={3}
              value={(p.payment_milestones || []).join("\n")}
              onChange={(v) => upd({ payment_milestones: v.split("\n").map((s) => s.trim()).filter(Boolean) })}
              ro={readOnly} testid="ed-milestones" />
          </Card>

          {/* 4. T&Cs */}
          <Card title="4. Additional Terms & Conditions" open={openSections.terms} onToggle={() => toggle("terms")}>
            {(p.additional_terms || []).map((t, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <span className="text-stone-500 text-xs pt-1.5 w-6">{i + 1}.</span>
                {readOnly ? <p className="flex-1 text-sm text-stone-700">{t}</p> :
                  <>
                    <textarea value={t} rows={2} onChange={(e) => {
                      const ts = [...p.additional_terms]; ts[i] = e.target.value; upd({ additional_terms: ts });
                    }} className="flex-1 px-2 py-1 bg-white border border-stone-200 rounded text-xs focus:outline-none focus:border-[#72BE44]" />
                    <button onClick={() => upd({ additional_terms: p.additional_terms.filter((_, x) => x !== i) })}
                      className="text-rose-500 hover:text-rose-700 mt-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>}
              </div>
            ))}
            {!readOnly && (
              <button onClick={() => upd({ additional_terms: [...(p.additional_terms || []), ""] })}
                className="text-xs text-[#72BE44] hover:underline">+ Add Term</button>
            )}
          </Card>

          {/* 5. Conclusion */}
          <Card title="5. Conclusion" open={openSections.conclusion} onToggle={() => toggle("conclusion")}>
            <ED textarea rows={6} value={p.conclusion} onChange={(v) => upd({ conclusion: v })} ro={readOnly} testid="ed-conclusion" />
          </Card>

          {p.approval_notes && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
              <b>Approval Notes:</b> {p.approval_notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, open, onToggle, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 hover:bg-stone-100 transition" type="button">
        <span className="font-semibold text-[#0F2A4A] text-sm">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold block mt-2 mb-1">{children}</label>;
}

function ED({ label, value, onChange, ro, textarea, rows = 2, testid }) {
  if (ro) {
    return (
      <div>
        {label && <Label>{label}</Label>}
        <div className="px-3 py-2 bg-stone-50 border border-stone-100 rounded text-sm text-stone-700 whitespace-pre-wrap">{value || "—"}</div>
      </div>
    );
  }
  return (
    <div>
      {label && <Label>{label}</Label>}
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
          className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-[#72BE44]" />
      )}
    </div>
  );
}

// Used by Leads page to open Proposal modal pre-filled with lead data
export function useProposalFromLead() {
  return (lead) => ({ lead_id: lead.id, client_name: lead.company_name, project_name: lead.project_name || lead.company_name });
}
